<template>
  <div v-loading="pageLoading" class="ds-container flex gap-20px">
    <template v-if="currentExamInfo.id">
      <div class="ds-aside">
        <el-upload class="avatar-uploader" :action="uploadAction" :headers="headers" :show-file-list="false"
          :before-upload="beforeAvatarUpload" :on-success="handleAvatarSuccess" :on-remove="handleRemove"
          :disabled="!isEditRegistrationInfo">
          <img v-if="formData.headImg" :src="formData.headImg" class="avatar">
          <i v-else class="el-icon-plus avatar-uploader-icon"></i>
          <template #tip>
            <div class="tip-text" style="font-size: 12px; color: #909399">
              <span style="color: red;">*</span>
              <span>一寸照</span>
              <span>jpg/png/webp格式</span>
              <span>大小不能超过500KB</span>
            </div>
          </template>
        </el-upload>
      </div>
      <div class="ds-main flex-1">
        <template v-if="isEditRegistrationInfo">
          <ds-form ref="dsFormRef" v-model="formData" v-bind="formConfig">
            <template #handle>
              <el-col>
                <el-button type="success" :loading="submitLoading" @click="onSubmit({ submitStatus: 0 })">
                  保存
                </el-button>
                <el-button type="primary" :loading="submitLoading" @click="onSubmit({ submitStatus: 1 })">
                  提交
                </el-button>
              </el-col>
            </template>
            <template v-if="formData.regRequirement" #regRequirement="{ formItem }">
              <el-col v-bind="formItem.colAttrs">
                <el-form-item v-bind="formItem">
                  <div v-html="formData.regRequirement"></div>
                </el-form-item>
              </el-col>
            </template>
          </ds-form>
        </template>
        <!-- 提交后（只读）：沿用与「提交前」完全相同的栅格 —— label 在上、两列（gutter 30）、
             报名要求这类字段独占一行，只把输入框换成纯文本，保证前后观感一致。
             原先这里用 el-descriptions（border 模式）：它内部是 table-layout:auto 的表格，
             列宽按内容分配，长内容（18 位身份证号等）会把表格撑得比容器还宽，
             于是右列被挤到屏幕外、标题折成两行，和提交前差别很大。 -->
        <template v-else>
          <el-form class="readonly-form" label-position="top">
            <el-row :gutter="30">
              <el-col v-for="formItem in formConfig.formItems" :key="formItem.prop"
                :span="isFullRowItem(formItem) ? 24 : 12">
                <el-form-item :label="formItem.label">
                  <div v-if="formItem.type === 'editor'" class="readonly-value"
                    v-html="registrationInfo[formItem.prop] || formItem.valueText"></div>
                  <a v-else-if="formItem.type === 'singleImageUpload' && isPdfFile(registrationInfo[formItem.prop] || formItem.valueText)"
                    :href="registrationInfo[formItem.prop] || formItem.valueText" target="_blank"
                    class="readonly-value">查看 PDF 文件</a>
                  <el-image v-else-if="formItem.type === 'singleImageUpload'"
                    :src="registrationInfo[formItem.prop] || formItem.valueText"
                    :preview-src-list="[registrationInfo[formItem.prop] || formItem.valueText]"
                    class="preview-image-small" style="width: 150px;"></el-image>
                  <el-table v-else-if="formItem.type === 'subTable'" :data="parseSubTableRows(formItem.valueJson)"
                    border size="small" style="width: 100%">
                    <el-table-column type="index" label="序号" width="60" align="center" />
                    <el-table-column v-for="col in formItem.columns || []" :key="col.field" :label="col.label">
                      <template slot-scope="{ row }">{{ row[col.field] || '-' }}</template>
                    </el-table-column>
                  </el-table>
                  <div v-else class="readonly-value">{{ readonlyValue(formItem) }}</div>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </template>
      </div>
    </template>
    <div v-else
      style=" display: flex; justify-content: center; align-items: center; flex-direction: column;min-height: calc(100vh - 120px);width: 100%;">
      <div class="print-btn">
        <i class="el-icon-circle-close" style="font-size: 66px;"></i>
      </div>
      <div style="margin-top: 30px;">暂无考试可报名！</div>
    </div>
  </div>
</template>

<script>
// api
import * as ExamRegistrationApi from "@/api/exam/registration";
// store
import { mapGetters } from "vuex";
// cpns
import DsForm from "@/base-ui/ds-form/index";
import { signature } from "@/services/request/sign";
import { getAccessToken } from "@/utils/auth";
// config
import { defaultFormItems } from "./config/form-config";
import { Message, MessageBox, Notification } from "element-ui";

export default {
  components: {
    DsForm,
  },
  data() {
    return {
      pageLoading: false,
      formItems: defaultFormItems,
      formConfig: {},
      formData: { headImg: "" },
      submitLoading: false,
      registrationInfo: null,
      auditStatus: undefined, // undefined-未提交 / 0待线上审 / 1待线下审 / 2已通过 / 3线上不通过 / 4线下不通过 / 5不符合条件
    };
  },
  computed: {
    ...mapGetters(["currentExamInfo", "currentExamId", "userInfo"]),
    uploadAction() {
      return process.env.VUE_APP_UPLOAD_API;
    },
    headers() {
      const signObj = signature();
      const config = {
        ...signObj,
        nonce: signObj.timestamp,
        ["tenant-id"]: 88,
      };
      // 设置 token：未登录时不带 Authorization（不再用 "Bearer test1" 兜底，避免误命中服务端模拟登录）
      if (getAccessToken()) {
        config["Authorization"] = "Bearer " + getAccessToken();
      }
      return config;
    },
    optionsValueToLabel() {
      return (options, value) =>
        options.find((item) => item.value === value)?.label;
    },
    isEditRegistrationInfo() {
      // 仅以下情况允许考生编辑/再次提交报名信息：
      //  - undefined：尚未提交过
      //  - submitStatus === 0：草稿（未提交）
      //  - 线上资格审查不符合条件（auditStatus === 5 且 twoAuditStatus !== 5）：可重新完善并再次提交
      // 线下资格审查不符合条件（auditStatus === 5 且 twoAuditStatus === 5）报名信息已锁定，不可修改；
      // 审核不通过（3 线上不通过 / 4 线下不通过）不允许再次提交考生信息
      const oneNotQualified =
        Number(this.auditStatus) === 5 &&
        Number(this.registrationInfo?.twoAuditStatus) !== 5;
      return (
        this.auditStatus === undefined ||
        this.registrationInfo?.submitStatus === 0 ||
        oneNotQualified
      );
    },
  },
  watch: {
    "formData.idCard": {
      handler(newVal, oldVal) {
        // 根据身份证号获取性别：18 位取第 17 位，15 位取第 15 位，奇数为男(1)，偶数为女(2)
        if (newVal) {
          const gender = this.parseGenderByIdCard(newVal);
          if (gender) {
            this.formData.gender = gender;
          }
        }
      },
      deep: true,
    },
    "formData.postId": {
      handler(newVal) {
        // 岗位代码跟随「报考岗位」自动回填（字段只读，考生不可编辑）
        const post = (this.currentExamInfo?.postList || []).find(
          (item) => item.id === newVal,
        );
        if (post) {
          this.formData.postCode = post.postCode || "";
        }
      },
    },
  },
  created() {
    if (!this.currentExamInfo.id) return;
    this.fetchMyRegistrationInfo();
  },
  methods: {
    async onSubmit(args) {
      let valid
      try {
        if (args.submitStatus === 1) {
          valid = await this.$refs.dsFormRef.$refs.elForm.validate();
          if (!valid) return;
        }
        // 未配置报名表单时 filedList 为 null，兜底为空数组，避免提交时报错
        // 联系电话走固定字段（params.phone），动态字段里的同名条目不再提交，避免重复写入
        const fields = (this.currentExamInfo?.filedList || [])
          .filter((item) => !this.isContactFieldItem(item))
          .map((item) => {
          // 子表格：行数据以 JSON 提交，可读摘要由后端统一生成
          if (item.dictType === "subTable") {
            const rows = Array.isArray(this.formData[item.columnField])
              ? this.formData[item.columnField]
              : [];
            return {
              examInfoFiledId: item.id,
              valueJson: JSON.stringify(rows),
            };
          }
          for (const key in this.formData) {
            if (key === item.columnField) {
              item.valueText = this.formData[key];
              item.valueLabel = this.formData[`${key}Label`];
            }
          }
          return {
            examInfoFiledId: item.id,
            valueText: item.valueText,
            valueLabel: item.valueLabel,
          };
        });
        const params = {
          ...this.formData,
          examId: this.currentExamId,
          fields,
        };

        this.submitLoading = true;
        if (args.submitStatus === 1) {
          MessageBox.confirm(
            "报名信息提交后不支持修改，请核对信息后确认提交",
            "提示",
            {
              confirmButtonText: "确认提交",
              cancelButtonText: "取消",
              type: "warning",
            },
          )
            .then(async () => {
              await ExamRegistrationApi.create({ ...params, ...args });
              this.$message.success("操作成功");
              this.fetchMyRegistrationInfo();
            })
            .catch(() => {
              return
            });
        } else {
          await ExamRegistrationApi.create({ ...params, ...args });
          this.$message.success("操作成功");
          this.fetchMyRegistrationInfo();
        }

      } catch (error) {
        console.log("error:", error);
        // 仅“提交”时表单校验未通过才提示必填；保存/接口报错不应误报成“必填项未填”
        if (args.submitStatus === 1 && !valid) {
          this.$message.warning("请检查必填项是否填写完整！");
        }
      } finally {
        this.submitLoading = false;
      }
    },
    onSave() { },
    handleRemove(file, fileList) {
      console.log(file, fileList);
    },
    beforeAvatarUpload(file) {
      console.log("beforeAvatarUpload:", file);
      // 一寸照仅支持 JPG/PNG/WEBP（后端 imageio-webp 会把 webp 转成 PNG 用于生成 PDF）
      const isValidFormat =
        file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/webp";
      const isLt500KB = file.size < 500 * 1024;

      if (!isValidFormat) {
        this.$message.error("一寸照仅支持 JPG/PNG/WEBP 格式!");
      }
      if (!isLt500KB) {
        this.$message.error("一寸照大小不能超过 500KB!");
      }
      return isValidFormat && isLt500KB;
    },
    handleAvatarSuccess(res) {
      this.formData.headImg = res.data;
    },
    // 上传控件允许 JPG/PNG/WEBP 与 PDF：PDF 不能按图片预览，只读时改为链接打开
    isPdfFile(url) {
      if (!url) return false;
      return url.toLowerCase().split("?")[0].endsWith(".pdf");
    },
    // 提交后只读视图的取值：性别 / 报考岗位按选项映射成文案，其余直接取报名数据
    // （与原先 el-descriptions 里的取值口径一致）
    readonlyValue(formItem) {
      const value = this.registrationInfo && this.registrationInfo[formItem.prop];
      if (formItem.prop === "gender" || formItem.prop === "postId") {
        return this.optionsValueToLabel(formItem.options || [], value) || "";
      }
      return value || formItem.valueLabel || formItem.valueText || "";
    },
    // 与「提交前」保持同一栅格：后台配置 colAttrs.span=24（或标记 onlyRow，即 span=2）的字段独占一行
    isFullRowItem(formItem) {
      return Number(formItem?.colAttrs?.span) === 24 || Number(formItem?.span) === 2;
    },
    // 根据身份证号解析性别：返回 "1" 男 / "2" 女，无法解析时返回 ""
    parseGenderByIdCard(idCard) {
      const value = (idCard || "").trim();
      // 18 位：第 17 位（倒数第二位）为性别位；15 位老身份证：第 15 位（最后一位）
      const genderChar =
        /^\d{17}[\dXx]$/.test(value)
          ? value.charAt(16)
          : /^\d{15}$/.test(value)
            ? value.charAt(14)
            : "";
      if (!genderChar) {
        return "";
      }
      return Number(genderChar) % 2 === 0 ? "2" : "1";
    },
    // 富文本是否视觉为空：去掉 HTML 标签与空白实体后无有效文字
    isRichTextEmpty(html) {
      if (!html) return true;
      const text = String(html)
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;|&#160;/gi, " ")
        .replace(/\s/g, "");
      return !text;
    },
    initFormConfig(config) {
      let formItems = [
        ...this.formItems,
        ...this.mapFormItems(config.formItems),
      ];
      const params = {};

      for (const formItem of formItems) {
        // 子表格是数组，其余字段保持原有空串初始化
        params[formItem.prop] = formItem.type === "subTable" ? [] : "";
        switch (formItem.prop) {
          case "postId":
            formItem.options =
              this.currentExamInfo?.postList?.map?.((item) => ({
                ...item,
                label: item.name,
                value: item.id,
              })) || [];
            break;
        }
      }

      // 报名要求为空（含 <p><br></p>、&nbsp; 等视觉为空的富文本）时不渲染该项
      if (this.isRichTextEmpty(this.currentExamInfo?.regRequirement)) {
        formItems = formItems.filter((item) => item.prop !== "regRequirement");
      }

      this.formConfig = {
        labelPosition: "top",
        formItems,
      };

      return params;
    },
    mapFormItems(formItems) {
      // 后台未配置「报名表单配置」时，接口返回的 filedList 为 null；
      // 若此处返回 undefined，上层 [...默认项, ...undefined] 会抛 TypeError，
      // 被 fetchMyRegistrationInfo 的 try/catch 吞掉，导致默认必填项（姓名/身份证号/性别/报考岗位）不渲染
      if (!Array.isArray(formItems)) return [];
      try {
        return formItems.map((item) => {
          // 是否选填：字典「是否允许为空」为真、标题为“其它条件/其他条件”，
          // 或字段描述声明了「如无可以不上传」等可缺省语义（字典必填开关与描述文案不一致时的兜底）
          const optional =
            !!item.validateNullable ||
            this.isOtherConditionField(item.columnName) ||
            this.isOptionalByComment(item.columnComment);
          // 子表格：列定义来自字典 columnsJson，行数范围来自 minRow/maxRow
          const isSubTable = item.dictType === "subTable";
          const subTableColumns = isSubTable ? this.parseSubTableColumns(item) : [];
          return {
            ...item,
            type: item.dictType,
            prop: item.columnField,
            label: item.columnName + item.columnComment,
            labelWidth: `${item.columnName.length * 14 + (optional ? 12 : 24)}px`,
            columns: subTableColumns,
            rules: isSubTable
              ? this.buildSubTableRules(item, subTableColumns)
              : optional
                ? []
                : [
                  {
                    required: true,
                    message: "请检查必填项",
                    trigger: ["blur", "change"],
                  },
                  // item.columnName == '联系电话' ? {
                  //   pattern: /^1[3456789]\d{9}$/,
                  //   message: "请输入正确的联系电话",
                  //   trigger: ["blur", "change"],
                  // } : {}
                ],
            // 子表格列较多，固定独占一行
            colAttrs: { span: isSubTable || item?.onlyRow ? 24 : 12 },
            span: isSubTable || item?.onlyRow ? 2 : 1,
          };
        })
          // 联系电话已升级为固定默认报名字段（见 config/form-config.js）：
          // 报名字典/表单配置里若还留着同名动态字段，这里直接忽略，避免页面上出现两个「联系电话」
          .filter((item) => !this.isContactFieldItem(item));
      } catch (error) {
        console.error("Error mapFormItems:", error);
        return [];
      }
    },
    /** 解析子表格的列定义（字典 columnsJson） */
    parseSubTableColumns(item) {
      const raw = item?.columnsJson;
      if (!raw) return [];
      try {
        const columns = typeof raw === "string" ? JSON.parse(raw) : raw;
        return Array.isArray(columns) ? columns : [];
      } catch (error) {
        console.error("Error parseSubTableColumns:", error);
        return [];
      }
    },
    /** 解析子表格的行数据（报名数据 valueJson） */
    parseSubTableRows(valueJson) {
      if (!valueJson) return [];
      try {
        const rows = typeof valueJson === "string" ? JSON.parse(valueJson) : valueJson;
        return Array.isArray(rows) ? rows : [];
      } catch (error) {
        console.error("Error parseSubTableRows:", error);
        return [];
      }
    },
    /** 子表格校验：行数范围 + 列必填（与后端校验口径一致） */
    buildSubTableRules(item, columns) {
      const minRow = Number(item?.minRow) || 0;
      const maxRow = Number(item?.maxRow) || 0;
      // 必填字段至少 1 行
      const requiredMin = !Number(item?.validateNullable) ? Math.max(minRow, 1) : minRow;
      return [
        {
          validator: (rule, value, callback) => {
            const rows = Array.isArray(value) ? value : [];
            if (rows.length < requiredMin) {
              callback(new Error(`至少填写 ${requiredMin} 行`));
              return;
            }
            if (maxRow > 0 && rows.length > maxRow) {
              callback(new Error(`最多填写 ${maxRow} 行`));
              return;
            }
            for (let i = 0; i < rows.length; i++) {
              const emptyCol = columns.find(
                (col) => Number(col.required) === 1 && !String(rows[i][col.field] || "").trim(),
              );
              if (emptyCol) {
                callback(new Error(`第 ${i + 1} 行「${emptyCol.label}」不能为空`));
                return;
              }
            }
            callback();
          },
          trigger: ["blur", "change"],
        },
      ];
    },
    /**
     * 是否为「联系电话」类字段。
     * 联系电话是固定默认报名字段，动态字段里同名的条目在渲染与提交时都要忽略；
     * 字段名不固定（mobile / phone / tel…），因此按字段标题或字段名匹配。
     */
    isContactFieldItem(item) {
      const label = String(item?.label || item?.columnName || "");
      const prop = String(item?.prop || item?.columnField || "");
      return (
        /联系电话|联系方式|手机号|手机号码/.test(label) ||
        /^(mobile|mobilePhone|phone|contactPhone|linkPhone|tel|telephone)$/i.test(prop)
      );
    },
    /** 判断是否为"其它条件/其他条件"字段（该字段为非必填） */
    isOtherConditionField(columnName) {
      if (!columnName) return false
      return columnName.indexOf("其它条件") !== -1 || columnName.indexOf("其他条件") !== -1
    },
    /**
     * 判断字段描述是否声明了「如无可以不上传」等可缺省语义。
     * 字典项的「是否允许为空」配置可能与描述文案不一致（描述说可不传、开关却为必填），
     * 此时以描述为准将字段视为选填，避免考生被误拦在提交环节。
     */
    isOptionalByComment(columnComment) {
      if (!columnComment) return false
      const text = String(columnComment).replace(/\s/g, "")
      return (
        /如无.{0,8}(可以不|可不|无需|不需|不用)/.test(text) ||
        text.indexOf("选填") !== -1 ||
        text.indexOf("非必填") !== -1
      )
    },
    /**
     * 用手机号注册的考生：把注册手机号回填到固定报名字段「联系电话」（formData.phone）。
     * - 仅在为空时回填：已有报名数据以报名数据为准，考生也可手工修改后提交；
     * - 注册账号是邮箱时（后端会把 userName 存进 mobile），不做回填。
     */
    fillContactMobile() {
      const mobile = String(this.userInfo?.mobile || "").trim();
      if (!/^1[3-9]\d{9}$/.test(mobile)) return;
      if (!this.formData.phone) {
        this.formData.phone = mobile;
      }
    },
    /**
     * @description 获得当前考生报名信息
     * */
    async fetchMyRegistrationInfo() {
      try {
        if (!this.currentExamId) return;
        console.log("fetchMyRegistrationInfo:", this.currentExamId);
        const res = await ExamRegistrationApi.get({
          examId: this.currentExamId,
        });
        this.registrationInfo = res;
        this.auditStatus = this.registrationInfo?.auditStatus;

        // init form
        const formItems = this.isEditRegistrationInfo
          ? this.currentExamInfo?.filedList
          : this.registrationInfo?.fields;
        this.formData = {
          ...this.formData,
          ...this.initFormConfig({ formItems }),
        };
        // 提交或未驳回
        if (this.registrationInfo) {
          (this.registrationInfo.fields || []).forEach((item) => {
            // 子表格回显：把 valueJson 解析成行数组，供表格控件编辑
            this.formData[item.columnField] =
              item.dictType === "subTable"
                ? this.parseSubTableRows(item.valueJson)
                : item.valueText;
          });
          this.formData.headImg = this.registrationInfo.headImg;
          this.formData.name = this.registrationInfo.name;
          this.formData.gender = this.registrationInfo.gender;
          this.formData.idCard = this.registrationInfo.idCard;
          this.formData.postId = this.registrationInfo.postId;
          // 岗位代码回显：取报名详情里的岗位代码（岗位被停用后仍能显示当时报的岗位）
          this.formData.postCode = this.registrationInfo.postCode;
          // 联系电话（固定字段）：优先取报名数据；兼容历史数据 —— 固定列没值时取动态字段里原有的「联系电话」
          this.formData.phone = this.registrationInfo.phone;
          if (!this.formData.phone) {
            const contactField = (this.registrationInfo.fields || []).find((item) =>
              this.isContactFieldItem(item),
            );
            this.formData.phone = contactField?.valueText || "";
          }
        }
        // 报名信息里没有身份证号时，默认回显注册时填写的身份证号
        if (!this.formData.idCard) {
          this.formData.idCard = this.userInfo?.idCard;
        }
        // 报名信息里没有姓名时，默认回显注册时填写的姓名（会员用户的「真实名字」）
        if (!this.formData.name) {
          this.formData.name = this.userInfo?.name;
        }
        // initFormConfig 中 params 会把 regRequirement 重置为 ""，故在其后还原为考试配置值
        this.formData.regRequirement = this.currentExamInfo?.regRequirement;
        // 用手机号注册的考生：把注册手机号回填到「联系电话」类字段
        // （放在已保存报名数据之后，仅在字段为空时回填，不覆盖考生已填写的内容，且仍可编辑）
        this.fillContactMobile();
      } catch (error) {
        console.error("Error fetchMyRegistrationInfo:", error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.avatar-uploader {
  :deep(.el-upload) {
    position: relative;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
  }

  :deep(.el-upload:hover) {
    border-color: #409eff;
  }

  .preview-image-small {
    width: 80px;
    height: 80px;
    object-fit: cover;
    cursor: pointer;
    border-radius: 4px;
  }

  .avatar {
    width: 250px;
    height: 350px;
  }

  .avatar-uploader-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 250px;
    height: 350px;
    font-size: 28px;
    color: #8c939d;
  }

  .tip-text {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

::v-deep .is-process {
  color: #409eff !important;
  border-color: #409eff !important;
}

.print-btn {
  width: 88px;
  height: 88px;
  background: #f5f7fa;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 提交后只读视图：值用纯文本展示，占位高度与提交前的输入框接近，保证前后两屏观感一致 */
.readonly-form .readonly-value {
  min-height: 32px;
  padding: 5px 0;
  line-height: 22px;
  color: #303133;
  word-break: break-word;
}

/* 平板端适配 */
@media screen and (max-width: 1024px) {
  .ds-container {
    flex-direction: column;
    gap: 10px;
  }

  .ds-aside {
    .avatar {
      width: 180px;
      height: 252px;
    }

    .avatar-uploader-icon {
      width: 180px;
      height: 252px;
      font-size: 24px;
    }
  }

  .ds-main {
    ::v-deep .el-col {
      width: 100% !important;
      max-width: 100% !important;
    }
  }
}

/* 手机端适配 */
@media screen and (max-width: 768px) {
  .ds-container {
    flex-direction: column;
    gap: 16px;
    padding: 12px;
  }

  .ds-aside {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    .avatar {
      width: 150px;
      height: 210px;
    }

    .avatar-uploader-icon {
      width: 150px;
      height: 210px;
      font-size: 22px;
    }

    .tip-text {
      font-size: 11px;
      gap: 6px;
    }
  }

  .ds-main {
    width: 100%;

    ::v-deep .el-col {
      width: 100% !important;
      max-width: 100% !important;
    }

    ::v-deep .el-descriptions {
      overflow-x: auto;
    }
  }
}
</style>
